import { CommonModule } from '@angular/common';
import { Component, input } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective, QaiButtonComponent } from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { ScoringWeightsComponent } from 'src/app/pages/self-serve/simulation-builder/components/scoring-weights/scoring-weights.component';
import { EditWeightingModalComponent } from 'src/app/pages/self-serve/simulation-builder/components/modals/edit-weighting/edit-weighting-modal.component';
import { ScoreDistributionChartComponent } from 'src/app/pages/self-serve/simulation-builder/components/score-distribution-chart/score-distribution-chart.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { ISimulationUpdate } from 'src/app/interfaces/self-serve/requests/isimulation-update';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import {
  IScoreWeight,
  SECTION_TO_WEIGHT_KEY,
  WEIGHT_KEY_TO_SECTION,
  SectionType,
} from 'src/app/interfaces/self-serve/isimulation-score';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';

@Component({
  selector: 'overall-scoring-section',
  standalone: true,
  templateUrl: './overall-section.component.html',
  styleUrls: ['./overall-section.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    ScoringWeightsComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    QaiButtonComponent,
    EditWeightingModalComponent,
    ScoreDistributionChartComponent,
    TranslocoModule
],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class OverallSectionComponent {
  sectionWeights: IScoreWeight[] = [];
  form!: FormGroup;
  overallSection!: FormGroup;
  callUuid: string = '';
  moduleUuid: string = '';
  isSelfAuthoring = input(false);
  openEditModal = false;
  modalWeights: IScoreWeight[] = [];
  readOnly: boolean = false;

  description: string =
    'The overall score will be generated from the weighted average of the following sections. All enabled section weights must add upto 100%.';
  overallPromptLabel: string =
    'Enter the prompt that will be used to score the Overall section of the role play. *';

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private scoringRefinementService: ScoringRefinementService,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.moduleUuid = this.route.snapshot.queryParamMap?.get('moduleUuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.loadFormAndSetupAutoSave();
    this.loadSectionWeights();
  }

  editWeighting(): void {
    this.modalWeights = this.sectionWeights.map((weight) => ({ ...weight }));
    this.openEditModal = true;
  }

  handleModalConfirm(updatedWeights: IScoreWeight[]): void {
    this.authorReactiveFormService.patchForm(
      this.callUuid,
      SimulationConstants.SCORING_SECTION,
      { sectionWeights: updatedWeights }
    );

    this.openEditModal = false;
  }

  handleModalClose(): void {
    this.openEditModal = false;
  }

  private setReadOnlyMode() {
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.overallScorePromptGroup?.get('promptText')
    );
  }

  get overallScorePromptGroup(): FormGroup {
    return this.form?.get(
      SimulationConstants.OVERALL_PROMPT_SECTION
    ) as FormGroup;
  }

  private loadFormAndSetupAutoSave(): void {
    this.authorReactiveFormService.getForm(this.callUuid).subscribe((form) => {
      this.form = form?.get(SimulationConstants.SCORING_SECTION) as FormGroup;
      this.overallSection = form?.get(
        SimulationConstants.OVERALL_PROMPT_PATH
      ) as FormGroup;
      this.setupAutoSave(
        this.overallSection,
        SimulationConstants.OVERALL_PROMPT_PATH
      );
      this.setupSectionWeightsSubscription();
      this.setReadOnlyMode();
    });
  }

  private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        this.authorReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue
        );

        const readOnly = this.authorReactiveFormService.isReadOnly(
          this.callUuid
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string
  ): void {
    if (this.isSelfAuthoring()) {
      this.scoringRefinementService.notifySaved(callUuid);
      return;
    }

    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authorReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
      });

      this.form.updateValueAndValidity();
    });
  }

  loadSectionWeights() {
    const existingWeights = this.form?.get(
      SimulationConstants.SCORING_WEIGHTS_SECTION
    )?.value;

    this.sectionWeights = existingWeights;
  }

  private setupSectionWeightsSubscription(): void {
    const sectionWeightsControl = this.form?.get(
      SimulationConstants.SCORING_WEIGHTS_SECTION
    );
    if (!sectionWeightsControl) return;

    sectionWeightsControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((updatedWeights: IScoreWeight[]) => {
        console.log('Section weights updated:', updatedWeights);
        if (updatedWeights && updatedWeights.length > 0) {
          this.onWeightsChange(updatedWeights);
        }
      });
  }

  onWeightsChange(updatedWeights: IScoreWeight[]) {
    this.sectionWeights = [...updatedWeights];

    this.form
      ?.get(SimulationConstants.SCORING_WEIGHTS_SECTION)
      ?.setValue(this.sectionWeights);

    this.authorReactiveFormService.applyScoringValidators(
      this.form,
      this.sectionWeights
    );

    const calcType =
      this.authorReactiveFormService.buildCalcTypeFromSectionWeights(
        this.sectionWeights
      );

    const payload: ISimulationUpdate = {
      uuid: this.callUuid,
      existingModuleUuid: this.moduleUuid,
      calcType: calcType,
    };

    this.sendRequestToUpdateSimulation(payload);
  }

  sendRequestToUpdateSimulation(payload: ISimulationUpdate): void {
    this.authorService.updateSimulation(payload).subscribe({
      next: (res: ISimulation) => {
        console.log(res);
        if (this.isSelfAuthoring()) {
          this.scoringRefinementService.notifySaved(this.callUuid);
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
